import { useCallback, useEffect, useState } from 'react';
import { ORE_MINT_ADDRESS } from '../lib/evore';

const SOL_MINT = 'So11111111111111111111111111111111111111112';
const ORE_MINT = ORE_MINT_ADDRESS.toBase58();

// Jupiter's free Price API. Lite endpoint is keyless and rate-limited per IP,
// which is fine here because sunset only fetches on page load + manual refresh.
const JUP_PRICE_URL =
  `https://lite-api.jup.ag/price/v3?ids=${SOL_MINT},${ORE_MINT}`;

export interface Prices {
  solUsd: number | null;
  oreUsd: number | null;
}

export interface PricesState {
  loading: boolean;
  error: string | null;
  prices: Prices;
  lastUpdatedAt: Date | null;
  refresh: () => Promise<void>;
}

/**
 * Read-only USD price fetch for SOL + ORE.
 *
 * RPC-only constraint (see memory `sunset-rpc-only`) means we go straight to a
 * public price endpoint instead of our backend. No polling — load on mount and
 * on user-triggered refresh, matching the rest of the sunset surface
 * (`sunset-no-rolling-refresh`). Prices that fail to parse come back as null
 * so the UI can degrade to "—" rather than blocking the whole dashboard.
 */
export function usePrices(): PricesState {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prices, setPrices] = useState<Prices>({ solUsd: null, oreUsd: null });
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(JUP_PRICE_URL);
      if (!res.ok) throw new Error(`Price fetch ${res.status}`);
      const json = (await res.json()) as Record<
        string,
        { usdPrice?: number } | null
      >;
      const parse = (mint: string): number | null => {
        const raw = json?.[mint]?.usdPrice;
        return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
      };
      setPrices({ solUsd: parse(SOL_MINT), oreUsd: parse(ORE_MINT) });
      setLastUpdatedAt(new Date());
    } catch (err) {
      console.error('[usePrices] load failed:', err);
      setError(
        err instanceof Error ? err.message : 'sunset.errors.loadPricesFailed'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { loading, error, prices, lastUpdatedAt, refresh: load };
}
